import React from "react";
import { useTranslation } from "react-i18next";
import type { PermissionCategory } from "../../domain/entities/Profile";
import { textStyles } from "@/lib/textStyles";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PermissionsCardProps {
  permissions: PermissionCategory[];
}

export const PermissionsCard: React.FC<PermissionsCardProps> = ({
  permissions,
}) => {
  const { t } = useTranslation("profile");
  const totalCount = permissions.reduce(
    (count, section) => count + section.items.length,
    0,
  );

  return (
    <div className="border rounded-xl p-4 space-y-4">
      <div>
        <h2 style={textStyles.heading3}>{t("permissions.title")}</h2>
        <p className="mt-1 text-gray-400" style={textStyles.body}>
          {t("permissions.count", { count: totalCount })} •{" "}
          {t("permissions.categories", { count: permissions.length })}
        </p>
      </div>

      <div className="space-y-6">
        {permissions.map((section) => (
          <div key={section.title} className="space-y-3">
            <h3 className="text-sm font-medium text-gray-600">
              {section.title}
            </h3>

            <div className="flex flex-wrap gap-2">
              {section.items.map((item) => (
                <span
                  key={`${section.title}-${item.label}`}
                  className="inline-flex w-fit max-w-full items-center justify-center rounded-lg bg-gray-100 px-2 py-[5px] text-center"
                >
                  {item.description ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="break-words cursor-default"
                          style={textStyles.body}
                        >
                          {item.label}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        {item.description}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="break-words" style={textStyles.body}>
                      {item.label}
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
